// generic API fetching
/* eslint-disable @typescript-eslint/no-explicit-any */

import moment, { DurationInputArg2 } from 'moment';
import { RefreshAuthResponse } from '@/types';
import { fetchWithTimeout, ObjectWithStringKeys } from './fetchWithTimeout';

const BASE_URL = '/api/';

type Options = ObjectWithStringKeys;

const defaultOptions: {
  credentials: RequestCredentials | undefined;
  headers: { [key: string]: string };
} = {
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
  },
};

async function basicFetch(
  url: string,
  method: string,
  options: Options,
  body: any = null
) {
  const fetchOptions: RequestInit = {
    ...defaultOptions,
    ...options,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
      Authorization: `Bearer ${
        localStorage.getItem('access_token')?.toString() || ''
      }`,
    },
    method,
  };

  if (body) {
    fetchOptions.body = JSON.stringify(body);
  }

  return fetchWithTimeout(url, fetchOptions);
}

/**
 * What a request that did not come back ok throws. The message is the one every caller has always
 * read; `status` carries the response's status code and `payload` its parsed body, for a caller
 * that has to act on a particular answer rather than only show its message. The Tax Profile page
 * reads both: a 409 from a profile write answers with the row that is live now, and the page offers
 * to keep that value or to overwrite it.
 */
export type ApiRequestError = Error & {
  status?: number;
  payload?: unknown;
  /**
   * The response's Retry-After header, or null when it had none. The Tax Profile page's Add
   * document modal waits this long before sending a file again after a 429.
   */
  retryAfter?: string | null;
};

const requestError = (
  message: string,
  status: number,
  payload: unknown,
  retryAfter: string | null = null
): ApiRequestError =>
  Object.assign(new Error(message), { status, payload, retryAfter });

async function fetchWithRetry<T = any>(
  url: string,
  method: string,
  options: Options,
  body: any = null,
  retry = true
): Promise<T> {
  const response = await basicFetch(url, method, options, body);
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return handleResponse<T>(response, url, method, options, body, retry);
}

const handleResponse = async <T = any>(
  r: Response,
  url: string,
  method: string,
  options: Options,
  body: any,
  retry: boolean
) => {
  try {
    if (r.ok) {
      return (await r.json()) as T;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payload = await r.json().catch(() => null);
    const json = payload as RefreshAuthResponse;
    const refreshAuthentication = json ? json.refreshAuthentication : undefined;

    if (r.status === 403 && refreshAuthentication && retry) {
      const { user, access_token: accessToken, maxAge } = refreshAuthentication;
      const date = moment()
        .add(maxAge[0], maxAge[1] as DurationInputArg2)
        .toDate();

      if (accessToken) localStorage.setItem('access_token', accessToken);

      localStorage.setItem('User', JSON.stringify(user));
      if (maxAge) localStorage.setItem('expiresIn', date.getTime().toString());
      localStorage.setItem('authentication_refreshed', 'true');

      return await fetchWithRetry<T>(url, method, options, body, false);
    }
    // `error` is read two ways because routes answer both: most send a sentence, a few send an
    // object carrying one. It is read through `?.` for the same reason the line below matters — a
    // body with neither `message` nor `error` used to reach `payload.error.message` and throw a
    // TypeError here, before the status was ever put on the error, so the catch below built a plain
    // Error with no status and nothing downstream could tell a 409 from any other refusal.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payloadMessage =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload?.message ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload?.error?.message ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      payload?.error ||
      undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const msg = payloadMessage ?? `API Error: ${r.statusText}`;
    throw requestError(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      msg,
      r.status,
      payload,
      r.headers?.get('Retry-After') ?? null
    );
  } catch (e: any) {
    // The throw above already carries the status and the body it was built from; rethrow it rather
    // than reading the response again, whose body has been consumed by then.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (typeof e?.status === 'number') throw e;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const payload = await r.json().catch(() => null);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const msg = payload?.message ?? e?.message;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    throw new Error(msg);
  }
};

const defaultOptionsForPostFiles: {
  credentials: RequestCredentials | undefined;
  headers: { [key: string]: string };
} = {
  credentials: 'same-origin',
  headers: {},
};

export function get<T = any>(endpoint: string, options: Options = {}) {
  return fetchWithRetry<T>(`${BASE_URL}${endpoint}`, 'GET', options);
}

export function put<T = any>(
  endpoint: string,
  body: any,
  options: Options = {}
) {
  return fetchWithRetry<T>(`${BASE_URL}${endpoint}`, 'PUT', options, body);
}

export function patch<T = any>(
  endpoint: string,
  body: any,
  options: Options = {}
) {
  return fetchWithRetry<T>(`${BASE_URL}${endpoint}`, 'PATCH', options, body);
}

export function post<T = any>(
  endpoint: string,
  body: any,
  options: Options = {}
) {
  return fetchWithRetry<T>(`${BASE_URL}${endpoint}`, 'POST', options, body);
}

export function postFiles(
  endpoint: string,
  body: FormData,
  options: Options = {}
) {
  return fetchWithTimeout(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    body,
    ...defaultOptionsForPostFiles,
    headers: {
      ...defaultOptionsForPostFiles.headers,
      Authorization: `Bearer ${
        localStorage.getItem('access_token')?.toString() || ''
      }`,
    },
    ...options,
  });
}

export function fetchDelete<T = any>(
  endpoint: string,
  body: any = {},
  options: Options = {}
) {
  return fetchWithRetry<T>(`${BASE_URL}${endpoint}`, 'DELETE', options, body);
}

export function head(endpoint: string, options: Options = {}) {
  return basicFetch(`${BASE_URL}${endpoint}`, 'HEAD', options);
}
